import { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { Panel, PanelHeader } from "@/components/common/Card";
import { GradePill, DirectionPill, StatusPill, ThesisInvalidationBadge } from "@/components/common/Pill";
import {
  getSignals,
  signalRMultiple,
  formatNum,
  formatSignedPct,
  formatR,
  timeAgo,
} from "@/lib/api";
import { SIGNALS } from "@/constants/testIds";
import { Funnel, Lightning, CaretRight } from "@phosphor-icons/react";

const STRATS = [
  { v: "", l: "All Strategies" },
  { v: "trend_pullback", l: "Trend Pullback" },
  { v: "breakout_retest", l: "Breakout Retest" },
  { v: "range_trading", l: "Range Trading" },
  { v: "liquidity_sweep", l: "Liquidity Sweep" },
];

const SelectInput = ({ value, onChange, options, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-[#ffffff] border border-slate-200 hover:border-slate-300 text-slate-900 text-xs font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  >
    {options.map((o) => (
      <option key={o.v} value={o.v}>
        {o.l}
      </option>
    ))}
  </select>
);

export const Signals = () => {
  const [direction, setDirection] = useState("");
  const [strategy, setStrategy] = useState("");
  const [grade, setGrade] = useState("");
  const [minScore, setMinScore] = useState("");

  const params = {};
  if (direction) params.direction = direction;
  if (strategy) params.strategy = strategy;
  if (grade) params.grade = grade;
  if (minScore) params.min_score = Number(minScore);

  const { data: signals, isLoading } = useSWR(
    ["/signals", params],
    () => getSignals({ ...params, limit: 200 }),
    { refreshInterval: 15000 },
  );

  return (
    <div className="flex flex-col gap-6 max-w-[1600px]">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">
            Live Feed
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">
            Signals
          </h1>
        </div>
        <div className="text-xs text-slate-500 font-data">
          {signals?.length || 0} results
        </div>
      </div>

      {/* Filters */}
      <Panel>
        <div className="flex flex-wrap items-center gap-3 p-4">
          <Funnel size={14} className="text-slate-500" />
          <SelectInput
            testid={SIGNALS.filterDirection}
            value={direction}
            onChange={setDirection}
            options={[
              { v: "", l: "All Directions" },
              { v: "LONG", l: "LONG" },
              { v: "SHORT", l: "SHORT" },
            ]}
          />
          <SelectInput
            testid={SIGNALS.filterStrategy}
            value={strategy}
            onChange={setStrategy}
            options={STRATS}
          />
          <SelectInput
            testid={SIGNALS.filterGrade}
            value={grade}
            onChange={setGrade}
            options={[
              { v: "", l: "All Grades" },
              { v: "A+", l: "A+" },
              { v: "A", l: "A" },
              { v: "B", l: "B" },
            ]}
          />
          <input
            data-testid="signals-filter-min-score"
            type="number"
            placeholder="Min Score"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            className="bg-[#ffffff] border border-slate-200 hover:border-slate-300 text-slate-900 text-xs font-data rounded-sm px-3 py-2 w-28 focus:border-[#2563eb] focus:outline-none"
          />
          {(direction || strategy || grade || minScore) && (
            <button
              data-testid="signals-clear-filters"
              onClick={() => {
                setDirection("");
                setStrategy("");
                setGrade("");
                setMinScore("");
              }}
              className="text-[10px] tracking-widest uppercase text-slate-600 hover:text-slate-900 px-2"
            >
              Clear
            </button>
          )}
        </div>
      </Panel>

      {/* Table */}
      <Panel>
        <PanelHeader title="All Signals" hint="Auto-refresh · 15s" />
        <div className="overflow-x-auto">
          <table data-testid={SIGNALS.table} className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
                <th className="text-left font-bold px-4 py-2.5">Pair</th>
                <th className="text-left font-bold px-4 py-2.5">Dir</th>
                <th className="text-left font-bold px-4 py-2.5">Strategy</th>
                <th className="text-left font-bold px-4 py-2.5">Grade</th>
                <th className="text-left font-bold px-4 py-2.5">Status</th>
                <th className="text-right font-bold px-4 py-2.5">Score</th>
                <th className="text-right font-bold px-4 py-2.5">Entry</th>
                <th className="text-right font-bold px-4 py-2.5">SL</th>
                <th className="text-right font-bold px-4 py-2.5">TP1</th>
                <th className="text-right font-bold px-4 py-2.5">RR</th>
                <th className="text-right font-bold px-4 py-2.5">PnL</th>
                <th className="text-left font-bold px-4 py-2.5">Regime</th>
                <th className="text-right font-bold px-4 py-2.5">Time</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={14}
                    className="px-4 py-10 text-center text-xs text-slate-400"
                  >
                    Loading…
                  </td>
                </tr>
              ) : (signals || []).length === 0 ? (
                <tr>
                  <td
                    colSpan={14}
                    className="px-4 py-12 text-center text-xs text-slate-400"
                  >
                    <Lightning
                      size={28}
                      className="mx-auto mb-2 text-slate-400"
                    />
                    No signals match your filters.
                  </td>
                </tr>
              ) : (
                signals.map((s) => (
                  <tr
                    key={s.id}
                    data-testid={SIGNALS.row(s.id)}
                    className="border-b border-slate-200/70 hover:bg-[#e2e8f0] transition-colors"
                  >
                    <td className="px-4 py-3 font-data text-slate-900">{s.pair}</td>
                    <td className="px-4 py-3">
                      <DirectionPill direction={s.direction} />
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {(STRATS.find((x) => x.v === s.strategy) || {}).l ||
                        s.strategy}
                    </td>
                    <td className="px-4 py-3">
                      <GradePill grade={s.grade} />
                    </td>
                    <td className="px-4 py-3" data-testid={`signal-status-${s.id}`}>
                      <div className="flex items-center gap-1.5">
                        <StatusPill status={s.status} />
                        <ThesisInvalidationBadge signal={s} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-data text-slate-900">
                      {formatNum(s.score, 1)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-slate-700">
                      {formatNum(s.entry)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-[#dc2626]">
                      {formatNum(s.stop_loss)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-[#16a34a]">
                      {formatNum(s.tp1)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-[#2563eb]">
                      {formatNum(s.rr, 2)}
                    </td>
                    <td
                      className="px-4 py-3 text-right font-data"
                      data-testid={`signal-pnl-${s.id}`}
                    >
                      {s.pnl_percent === null || s.pnl_percent === undefined ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="leading-tight">
                          <div
                            className={
                              s.pnl_percent >= 0
                                ? "text-[#16a34a]"
                                : "text-[#dc2626]"
                            }
                          >
                            {formatSignedPct(s.pnl_percent)}
                          </div>
                          {signalRMultiple(s) !== null && (
                            <div className="text-[10px] text-slate-500">
                              {formatR(signalRMultiple(s))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-[11px] tracking-wide">
                      {s.market_regime}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-500 text-xs">
                      {timeAgo(s.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/signals/${s.id}`}
                        data-testid={`signal-detail-link-${s.id}`}
                        className="inline-flex items-center text-[10px] tracking-widest uppercase text-[#2563eb] hover:text-slate-900"
                      >
                        View <CaretRight size={10} weight="bold" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};
